import type { ReactNode } from 'react';

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/shared/ui/shadcn/resizable';
import { cn } from '@/lib/utils';

interface ResizableSplitPaneProps {
  /** Content for the first (left or top) panel */
  first: ReactNode;
  /** Content for the second (right or bottom) panel */
  second: ReactNode;
  /** Direction of the split */
  direction?: 'horizontal' | 'vertical';
  /** Default size of the first panel (percentage, 0-100) */
  defaultFirstSize?: number;
  /** Minimum size of the first panel (percentage, 0-100) */
  minFirstSize?: number;
  /** Maximum size of the first panel (percentage, 0-100) */
  maxFirstSize?: number;
  /** Minimum size of the second panel (percentage, 0-100) */
  minSecondSize?: number;
  /** Maximum size of the second panel (percentage, 0-100) */
  maxSecondSize?: number;
  /** Show a visible drag handle on the resize bar */
  withHandle?: boolean;
  /** Called when panel sizes change */
  onResize?: (sizes: { first: number; second: number }) => void;
  /** Additional CSS classes for the container */
  className?: string;
  /** ID for the panel group (useful for persistence) */
  id?: string;
}

/** Convert a percentage number (0-100) to a string format for react-resizable-panels v4 */
function toPercentage(value: number): string {
  return `${value}%`;
}

/**
 * A two-panel resizable split layout built on shadcn's resizable component.
 * Provides sensible defaults for building workspace layouts.
 *
 * @example
 * ```tsx
 * <ResizableSplitPane
 *   direction="horizontal"
 *   defaultFirstSize={60}
 *   minFirstSize={30}
 *   withHandle
 *   first={<LeftPanel />}
 *   second={<RightPanel />}
 * />
 * ```
 */
export function ResizableSplitPane({
  first,
  second,
  direction = 'horizontal',
  defaultFirstSize = 50,
  minFirstSize = 20,
  maxFirstSize = 80,
  minSecondSize = 20,
  maxSecondSize = 80,
  withHandle = false,
  onResize,
  className,
  id,
}: ResizableSplitPaneProps) {
  const handleLayoutChange = (layout: { [panelId: string]: number }) => {
    if (onResize === undefined) {
      return;
    }

    const firstSize = layout['first'] ?? defaultFirstSize;
    const secondSize = layout['second'] ?? 100 - defaultFirstSize;
    onResize({ first: firstSize, second: secondSize });
  };

  return (
    <ResizablePanelGroup
      id={id}
      orientation={direction}
      onLayoutChange={handleLayoutChange}
      className={cn('h-full w-full', className)}
    >
      <ResizablePanel
        id="first"
        defaultSize={toPercentage(defaultFirstSize)}
        minSize={toPercentage(minFirstSize)}
        maxSize={toPercentage(maxFirstSize)}
        className="overflow-hidden bg-background"
      >
        {first}
      </ResizablePanel>
      <ResizableHandle withHandle={withHandle} />
      <ResizablePanel
        id="second"
        defaultSize={toPercentage(100 - defaultFirstSize)}
        minSize={toPercentage(minSecondSize)}
        maxSize={toPercentage(maxSecondSize)}
        className="overflow-hidden bg-background"
      >
        {second}
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
